import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapTrifold, Gavel, TrendUp, ChartLineUp, Buildings, Target } from "@phosphor-icons/react";
import { ServiceIconTile } from "@/components/ServiceIcon";

export default function Landing() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 bg-grain">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-40 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 relative">
          <div className="flex items-center gap-5 mb-8">
            <img
              src="/brand/immoscope-icon.png"
              alt="ImmoScope"
              className="h-28 w-28 sm:h-32 sm:w-32 object-contain"
              data-testid="landing-logo"
            />
            <div className="flex items-baseline">
              <span className="font-display font-black text-4xl sm:text-5xl tracking-[0.14em] text-lime-400">IMMO</span>
              <span className="font-display font-black text-4xl sm:text-5xl tracking-[0.14em] text-zinc-50">SCOPE</span>
            </div>
          </div>
          <div className="flex items-center gap-2 mb-6">
            <span className="w-2 h-2 bg-lime-400 animate-pulse" />
            <span className="font-mono-data text-xs text-lime-400 tracking-wider">LIVE · TERMINALE IMMOBILIARE ITALIANO</span>
          </div>

          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter max-w-4xl">
            Trova occasioni immobiliari<br />
            <span className="text-lime-400">prima</span> del mercato.
          </h1>

          <p className="mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            ImmoScope incrocia inserzioni di vendita, canoni di affitto, aste e pignoramenti
            in una griglia cartografica ad alta densità. Individua le zone con il miglior
            rapporto <span className="text-zinc-100 font-mono-data">€/m² / rendimento</span> e
            scopri le occasioni nelle aree di interesse.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold px-8 h-12"
                data-testid="hero-cta-register"
              >
                Inizia gratuitamente →
              </Button>
            </Link>
            <Link to="/login">
              <Button
                size="lg"
                variant="outline"
                className="rounded-none border-white/20 text-zinc-100 hover:bg-white/5 h-12"
                data-testid="hero-cta-login"
              >
                Accedi al terminale
              </Button>
            </Link>
          </div>

          {/* stats strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 border border-white/10 divide-x divide-white/10">
            {[
              ["INSERZIONI", "2.6K+"],
              ["CITTÀ", "5"],
              ["ASTE ATTIVE", "150+"],
              ["CELLE GRIGLIA", "196/città"],
            ].map(([k, v]) => (
              <div key={k} className="p-5">
                <div className="font-mono-data text-[10px] text-zinc-500 tracking-widest">{k}</div>
                <div className="font-display font-black text-2xl text-lime-400 mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services (4 branded icons) */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="mb-12 flex items-end justify-between gap-8 flex-wrap">
            <div>
              <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-3">— COSA FA IMMOSCOPE</div>
              <h2 className="font-display font-black text-4xl max-w-2xl leading-tight">Quattro strumenti. Una decisione informata.</h2>
            </div>
            <p className="text-sm text-zinc-400 max-w-md leading-relaxed">
              Ogni sezione dell'app risponde a una domanda precisa dell'investitore immobiliare.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10 divide-x divide-y sm:divide-y-0 divide-white/10" data-testid="services-grid">
            <Link to="/dashboard" className="group">
              <ServiceIconTile
                name="market"
                title="Market Analysis"
                subtitle="Griglia cartografica con €/m², canoni e rendimento per ogni cella."
                className="h-full group-hover:bg-lime-400/[0.03]"
              />
            </Link>
            <Link to="/confronto" className="group">
              <ServiceIconTile
                name="valuation"
                title="Property Valuation"
                subtitle="Analisi side-by-side con cap rate, cash-on-cash e payback."
                className="h-full group-hover:bg-lime-400/[0.03]"
              />
            </Link>
            <Link to="/ricerca" className="group">
              <ServiceIconTile
                name="insights"
                title="Data Insights"
                subtitle="Ricerca ideale, trend storici, verdetti su ogni zona."
                className="h-full group-hover:bg-lime-400/[0.03]"
              />
            </Link>
            <Link to="/preferiti" className="group">
              <ServiceIconTile
                name="tracking"
                title="Investment Tracking"
                subtitle="Portfolio, alert Telegram/email e riepilogo KPI aggregati."
                className="h-full group-hover:bg-lime-400/[0.03]"
              />
            </Link>
          </div>
        </div>
      </section>

      {/* Features detail */}
      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-3 gap-0 border border-white/10">
            {[
              {
                icon: <MapTrifold size={28} weight="bold" />,
                title: "Griglia cartografica",
                text: "Ogni cella mostra €/m² vendita, canone medio e rendimento lordo annuo. Dal ciano (basso) al lime (alto).",
              },
              {
                icon: <Gavel size={28} weight="bold" />,
                title: "Aste & pignoramenti",
                text: "Base d'asta, valore di mercato, sconto percentuale. Ordinato per convenienza, con dati del tribunale.",
              },
              {
                icon: <TrendUp size={28} weight="bold" />,
                title: "Motore opportunità",
                text: "Incrocia aree ad alto rendimento con aste in vendita. Nessun rumore, solo occasioni.",
              },
            ].map((f) => (
              <div key={f.title} className="p-8 border-r border-b border-white/10 last:border-r-0 hover:bg-white/[0.02] transition-colors">
                <div className="text-lime-400 mb-4">{f.icon}</div>
                <h3 className="font-display font-bold text-xl mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-white/10 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-3">— FLUSSO OPERATIVO</div>
          <h2 className="font-display font-black text-4xl mb-12 max-w-2xl">Dal dato grezzo alla decisione, in tre passi.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", icon: <Buildings size={22} weight="bold" />, t: "Definisci l'area", d: "Scegli una città e delimita la zona da monitorare sulla mappa." },
              { n: "02", icon: <ChartLineUp size={22} weight="bold" />, t: "Analizza la griglia", d: "Visualizza celle colorate per rendimento e identifica hotspot di valore." },
              { n: "03", icon: <Target size={22} weight="bold" />, t: "Colpisci l'occasione", d: "Filtra aste e inserzioni nelle celle top-yield e salva le più interessanti." },
            ].map((s) => (
              <div key={s.n} className="border border-white/10 p-8 bg-zinc-900/40">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-mono-data text-4xl text-zinc-600">{s.n}</span>
                  <span className="text-lime-400">{s.icon}</span>
                </div>
                <h3 className="font-display font-bold text-lg mb-2">{s.t}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-between items-center gap-4">
          <div className="font-mono-data text-xs text-zinc-500">
            IMMOSCOPE · TERMINAL v0.2 · DATI SIMULATI PER DEMO
          </div>
          <div className="font-mono-data text-xs text-zinc-600">© 2026</div>
        </div>
      </footer>
    </div>
  );
}
